import {Injectable} from '@angular/core';
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {catchError, filter, map, Observable, of, shareReplay, switchMap, take, tap} from "rxjs";

import {AuthenticationService} from '../service/auth.service';

/**
 * Response of GET /feature-flags — flags enabled for the current user/tenant.
 */
interface FeatureFlagsResponse {
    flags?: string[];
}

/**
 * Claims the feature flags enabled for the current user/tenant (e.g. "AGENTIC_CHAT").
 *
 * The flag set is fetched lazily on first use and cached for the whole session.
 * Any error (including 404 while the endpoint is not deployed yet) is treated as
 * an empty flag set — every flag reads as disabled — so the app never breaks.
 */
@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {

    private readonly apiUrl = environment.apiUrl + '/feature-flags';

    /** Session-cached flag set, shared by all subscribers. */
    private flags$?: Observable<Set<string>>;

    /** Snapshot of the loaded flag set; stays empty until the request resolves. */
    private loadedFlags = new Set<string>();

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthenticationService,
    ) {
    }

    /**
     * Emits whether the given flag is enabled for the current user/tenant.
     * Triggers the (session-cached) HTTP request on first use.
     */
    public isEnabled(flag: string): Observable<boolean> {
        return this.flags().pipe(map(flags => flags.has(flag)));
    }

    /**
     * Synchronous check backed by the already loaded flag set.
     * Returns false until the flags have arrived — prefer {@link isEnabled}
     * when the calling code can work with an Observable.
     */
    public isEnabledSnapshot(flag: string): boolean {
        this.ensureLoaded();
        return this.loadedFlags.has(flag);
    }

    /**
     * The session-cached flag set for the current user/tenant.
     * Triggers the (session-cached) HTTP request on first use.
     */
    public flags(): Observable<Set<string>> {
        if (!this.flags$) {
            // Fire the HTTP request only AFTER Firebase auth restored a user. The left
            // sidebar consumes flags() on every page load, so an eager request would race
            // token restoration on refresh, come back 401 and trip the error interceptor's
            // auto-logout (the known "call before auth ready" pitfall).
            this.flags$ = this.authService.user$.pipe(
                filter((user): user is NonNullable<typeof user> => !!user),
                take(1),
                switchMap(() => this.http.get<FeatureFlagsResponse>(this.apiUrl)),
                map(response => new Set(response?.flags ?? [])),
                catchError(error => {
                    console.warn('Failed to load feature flags, treating all flags as disabled', error);
                    return of(new Set<string>());
                }),
                tap(flags => this.loadedFlags = flags),
                shareReplay(1)
            );
        }
        return this.flags$;
    }

    /** Kicks off the flag request; no-op when it has already been started. */
    private ensureLoaded(): void {
        this.flags().pipe(take(1)).subscribe();
    }
}
